import React, { useEffect, useState } from 'react'
import {
  Button,
  Container,
  Image,
  ListContainer,
  TextBody,
  TextCaption,
  Popup,
  ToastContext
} from '@pmi/pmi-iqos-ds-react'
import { IqosphereApiManager } from '@iqosphere-common/services'

const AvatarFormPopup = ({
  isOpen,
  onClose,
  setOpenModal,
  userInfo = {},
  avatarList = [],
  isUpdate = false,
  loadData = () => {},
  line,
  translations
}) => {
  const [selectedAvatarCode, setSelectedAvatarCode] = useState(
    userInfo.avt.code
  )
  const [selectedAccessoryCode, setSelectedAccessoryCode] = useState(
    userInfo.avt.accessory
  )
  const [accessoryImageSelected, setAccessoryImageSelected] = useState(null)
  const [avatarSelected, setAvatarSelected] = useState([])
  const [avatarAccessories, setAvatarAccessories] = useState([])

  const [loading, setLoading] = useState(false)

  const api = new IqosphereApiManager(line)
  const popupToast = ToastContext.getContextActions().popupToast

  useEffect(() => {
    const selectedAvatar = avatarList.find(
      avatar => avatar.avatar_code === userInfo.avt.code
    )
    setAvatarSelected(selectedAvatar)

    if (isUpdate) {
      const fetchedAccessory =
        selectedAvatar?.accessories.find(
          accessory => accessory.accessory_code === userInfo.avt.accessory
        )?.accessory_code || ''

      setAvatarAccessories(selectedAvatar?.accessories || [])
      setSelectedAccessoryCode(fetchedAccessory)
    }
  }, [])

  const onUpdateAvatar = async () => {
    setLoading(true)
    try {
      await api.iqosphereProfile.updateAvatarProfile({
        avatar: selectedAvatarCode,
        accessory: selectedAccessoryCode || ''
      })
      loadData()
      setOpenModal(false)
    } catch (err) {
      console.error(err)
      popupToast({
        type: ToastContext.types.error,
        subtitle: translations.tk_updateError
      })
    } finally {
      setLoading(false)
    }
  }

  const onSelectAvatar = (data, isAvatar) => {
    if (isAvatar) {
      const selectedAvatar = avatarList.find(
        avatar => avatar.avatar_code === data.avatar_code
      )

      setSelectedAvatarCode(data.avatar_code)
      setSelectedAccessoryCode('')

      setAvatarSelected(selectedAvatar)
      setAvatarAccessories(selectedAvatar?.accessories || [])
      setAccessoryImageSelected(null)
      return
    }

    // is an accessory
    const selectedAccessory =
      avatarSelected?.accessories.find(
        acc => acc.accessory_code === data.accessory_code
      ) || null

    setSelectedAvatarCode(avatarSelected.avatar_code)
    setSelectedAccessoryCode(selectedAccessory?.accessory_code || '')

    setAvatarSelected(avatarSelected)
    setAvatarAccessories(avatarSelected?.accessories || [])
    setAccessoryImageSelected(selectedAccessory?.src || null)
  }

  const renderAvatar = ({
    selectedImage,
    avatarImage,
    avatarNickname,
    avatarCode
  }) => {
    return (
      <Container position={Container.positions.relative}>
        {selectedImage === avatarCode && (
          <Container
            width="24"
            height="6"
            display={Container.displays.flex}
            justifyContent={Container.justifyContentValues.end}
            position={Container.positions.absolute}>
            <Container
              width="6"
              height="full"
              t="2"
              r="2"
              bg={Container.backgrounds.white}
              rounded={Container.roundedSizes.full}
              position={Container.positions.absolute}
            />
            <Container
              width="4"
              height="4"
              t="3"
              r="3"
              bg={Container.backgrounds.turquoise}
              rounded={Container.roundedSizes.full}
              position={Container.positions.relative}
            />
          </Container>
        )}

        <Image src={avatarImage} width="20" rounded={Image.roundedSizes.full} />
        {!!avatarNickname && (
          <TextBody
            breakWords
            alignment={TextBody.alignments.center}
            size={TextBody.sizes.xs}>
            {avatarNickname}
          </TextBody>
        )}
      </Container>
    )
  }

  return (
    <Popup isOpen={isOpen} onCloseHandler={onClose} blocking>
      <Container
        display={Container.displays.flex}
        flexDirection={Container.flexDirections.column}
        fullWidth>
        <Container
          fullWidth
          flexShrink={2}
          display={Container.displays.flex}
          flexDirection={Container.flexDirections.column}
          alignItems={Container.alignItemsValues.center}>
          <Image
            src={accessoryImageSelected || avatarSelected.base_avatar_src || ''}
            width="40"
            rounded={Image.roundedSizes.full}
            contain
          />
          <Container pt="4" flexShrink={2} display={Container.displays.flex}>
            <TextCaption size={TextCaption.sizes.l}>
              {translations.tk_nickname}:
            </TextCaption>
            <TextBody size={TextBody.sizes.l} additionalClasses="q-pl-2">
              {avatarSelected?.nickname || ''}
            </TextBody>
          </Container>
        </Container>

        <Container py="4">
          <TextBody size={TextBody.sizes.xs}>
            {translations.tk_description}
          </TextBody>
          <TextBody size={TextBody.sizes.xs}>
            {translations.tk_descriptionSubtext}
          </TextBody>
        </Container>

        {/* Avatar Selection */}
        <ListContainer
          columns={4}
          justifyContent={ListContainer.justifyContentValues.center}
          xBetween="4"
          yBetween="4">
          {avatarList.map((avatar, index) => (
            <Container
              key={index}
              display={Container.displays.flex}
              flexDirection={Container.flexDirections.column}
              alignItems={Container.alignItemsValues.center}
              onClickHandler={() => onSelectAvatar(avatar, true)}>
              {renderAvatar({
                selectedImage: selectedAvatarCode,
                avatarImage: avatar.base_avatar_src,
                avatarNickname: avatar.nickname,
                avatarCode: avatar.avatar_code
              })}
            </Container>
          ))}
        </ListContainer>

        {/* Accessory selection */}
        {isUpdate && !!avatarAccessories.length && (
          <Container pt="6">
            <TextCaption additionalClasses="q-pb-4" size={TextCaption.sizes.l}>
              {translations.tk_myAccessories}:
            </TextCaption>
            <ListContainer
              columns={4}
              justifyContent={ListContainer.justifyContentValues.center}
              xBetween="4"
              yBetween="4">
              {avatarAccessories.map((accessory, index) => (
                <Container
                  key={index}
                  display={Container.displays.flex}
                  flexDirection={Container.flexDirections.column}
                  alignItems={Container.alignItemsValues.center}
                  onClickHandler={() => onSelectAvatar(accessory, false)}>
                  {renderAvatar({
                    selectedImage: selectedAccessoryCode,
                    avatarImage: accessory.src,
                    avatarCode: accessory.accessory_code
                  })}
                </Container>
              ))}
            </ListContainer>
          </Container>
        )}

        <Container
          pt="10"
          fullWidth
          display={Container.displays.flex}
          flexShrink={2}
          flexDirection={Container.flexDirections.column}
          alignItems={Container.alignItemsValues.center}>
          <Button
            variant={Button.variants.primary}
            onClickHandler={onUpdateAvatar}
            text={translations.tk_next}
            disabled={!selectedAvatarCode || loading}
            loading={loading}
          />
          <Container pt="4">
            <Button
              variant={Button.variants.secondary}
              onClickHandler={onClose}
              text={translations.tk_skip}
            />
          </Container>
        </Container>
      </Container>
    </Popup>
  )
}

export default AvatarFormPopup
